import { FeatureSwitch } from '@if/core/types'
import { formatDateTime } from '@if/core/utils'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  alpha,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useUpdateFeatureSwitch } from '@src/data/api/config/config-api'
import { useCurrentLang } from '@src/data/selectors/app-config-selectors'
import { ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

const STALE_THRESHOLD_MS = 21 * 24 * 60 * 60 * 1000

interface Props {
  featureSwitch: FeatureSwitch
  onUpdateResult: (success: boolean) => void
}

const FeaturesSwitch = ({ featureSwitch, onUpdateResult }: Props) => {
  const theme = useTheme()
  const lang = useCurrentLang()
  const [checked, setChecked] = useState(featureSwitch.isActivated)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingChecked, setPendingChecked] = useState(false)

  const [updateFeatureSwitch, isUpdating] = useUpdateFeatureSwitch()
  const { t } = useTranslation()

  const featureName = lang === 'fr' ? featureSwitch.nameFr : featureSwitch.nameEn

  const isStale =
    featureSwitch.activatedOn === null ||
    Date.now() - new Date(featureSwitch.activatedOn).getTime() > STALE_THRESHOLD_MS

  const staleTooltip =
    featureSwitch.activatedOn === null
      ? t('featureSwitch.staleNeverActivated')
      : t('featureSwitch.staleWarning')

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPendingChecked(event.target.checked)
    setConfirmOpen(true)
  }

  const handleConfirm = async () => {
    setConfirmOpen(false)
    setChecked(pendingChecked)

    try {
      await updateFeatureSwitch({ ...featureSwitch, isActivated: pendingChecked })
      onUpdateResult(true)
    } catch {
      setChecked(!pendingChecked)
      onUpdateResult(false)
    }
  }

  return (
    <>
      <ListItem
        sx={{
          backgroundColor: checked ? alpha(theme.palette.success.main, 0.08) : 'transparent',
          transition: 'background-color 0.3s',
        }}
      >
        <ListItemIcon sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 'auto', mr: 1.5 }}>
          <Chip
            label={t('featureSwitch.userStory', { id: featureSwitch.workItemId })}
            size="small"
            variant="outlined"
            component="a"
            href={`https://dev.azure.com/IcebergFinance/IcebergFinance/_workitems/edit/${featureSwitch.workItemId}`}
            target="_blank"
            rel="noopener noreferrer"
            clickable
          />
          {isStale && (
            <Tooltip title={staleTooltip}>
              <WarningAmberIcon color="warning" fontSize="small" />
            </Tooltip>
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <>
              {featureName}{' '}
              <Chip
                label={checked ? t('featureSwitch.active') : t('featureSwitch.inactive')}
                color={checked ? 'success' : 'default'}
                size="small"
                sx={{ ml: 1 }}
              />
            </>
          }
          slotProps={{ secondary: { variant: 'body2', component: 'div' } }}
          secondary={
            featureSwitch.activatedByUserFullName === null ? (
              t('featureSwitch.neverActivated')
            ) : (
              <>
                <Typography variant="inherit">
                  {t('featureSwitch.lastModifiedBy')} {featureSwitch.activatedByUserFullName}
                </Typography>
                <Typography variant="inherit">
                  {t('featureSwitch.lastModifiedOn')} {formatDateTime(featureSwitch.activatedOn)}
                </Typography>
              </>
            )
          }
        />
        <Switch checked={checked} onChange={handleChange} disabled={isUpdating} />
      </ListItem>
      <Divider />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('featureSwitch.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingChecked
              ? t('featureSwitch.confirmActivate', { name: featureName })
              : t('featureSwitch.confirmDeactivate', { name: featureName })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('featureSwitch.cancel')}</Button>
          <Button onClick={handleConfirm} variant="contained" autoFocus>
            {t('featureSwitch.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default FeaturesSwitch
